import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Analytics from 'analytics/client';
import { Events, EventName } from 'analytics/events';
import { RootState } from 'store';

/**
 * Reusable hook to fire a marketplace-scoped one-shot analytics event.
 * It ensures:
 *  - Only tracked once per marketplace (localStorage key event_one_shot_<event>_<marketplaceId>)
 *  - Optional gating so it only fires while onboarding team setup is incomplete.
 *
 * @param eventName      The event constant from Events.
 * @param shouldFire     Condition that tells the hook the triggering action occurred.
 * @param payload        Additional event properties (marketplace_id auto-injected if available).
 * @param options        Optional behavior flags.
 */
export const useOneShotMarketplaceEvent = (
    eventName: EventName,
    shouldFire: boolean,
    payload: Record<string, any>,
    options?: {
        gateOnboardingTeamIncomplete?: boolean; // if true, only fire while teamDone === false
    }
) => {
    const marketplaceId = useSelector((state: RootState) => state.marketplace.marketplace?.id);
    const onboardingSteps = useSelector((state: RootState) => state.onboardingSteps.onboardingSteps);

    useEffect(() => {
        if (!shouldFire) return;
        if (!marketplaceId) return; // wait until marketplace id exists
        if (options?.gateOnboardingTeamIncomplete && onboardingSteps.teamDone) return; // onboarding finished

        try {
            const key = `event_one_shot_${eventName}_${marketplaceId}`;
            if (localStorage.getItem(key) === '1') return; // already tracked

            Analytics.track(eventName as any, {
                marketplace_id: marketplaceId,
                ...payload
            });
            try {
                localStorage.setItem(key, '1');
            } catch (e) {
                // non-blocking
            }
        } catch (e) {
            // non-blocking
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFire, marketplaceId, eventName, options?.gateOnboardingTeamIncomplete]);
};

export default useOneShotMarketplaceEvent;
